import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { useChatDictation } from './useChatDictation';

type UseComposerDictationOptions = {
  /** Container of the composer textarea, used to restore focus after dictation. */
  composerBodyRef: RefObject<HTMLElement | null>;
  input: string;
  onChangeInput: (value: string) => void;
  onError: (message: string) => void;
  scopeKey?: string;
};

export function appendDictatedText(current: string, transcript: string) {
  return current.trim() ? `${current.replace(/\s+$/, '')} ${transcript}` : transcript;
}

/**
 * Composer glue around `useChatDictation`: appends the transcript to the draft
 * (never sends it), puts the caret at the end, and maps Enter / Escape while recording.
 */
export function useComposerDictation({
  composerBodyRef,
  input,
  onChangeInput,
  onError,
  scopeKey,
}: UseComposerDictationOptions) {
  const inputRef = useRef(input);
  const focusAfterDictationRef = useRef(false);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  const handleTranscript = useCallback((text: string) => {
    focusAfterDictationRef.current = true;
    onChangeInput(appendDictatedText(inputRef.current, text));
  }, [onChangeInput]);

  const handleError = useCallback((message: string) => {
    // The draft was never touched: only focus comes back.
    focusAfterDictationRef.current = true;
    onError(message);
  }, [onError]);

  const dictation = useChatDictation({ onError: handleError, onTranscript: handleTranscript, scopeKey });
  const { cancel, finish, status } = dictation;

  useEffect(() => {
    if (status !== 'idle' || !focusAfterDictationRef.current) {
      return;
    }
    focusAfterDictationRef.current = false;
    const textarea = composerBodyRef.current?.querySelector('textarea');
    if (textarea) {
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [composerBodyRef, input, status]);

  useEffect(() => {
    if (status !== 'recording') {
      return;
    }
    // Enter transcribes (never sends) and Escape cancels wherever the focus is;
    // a focused button keeps its native activation.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        cancel();
        return;
      }
      const target = event.target instanceof Element ? event.target : null;
      if (event.key !== 'Enter' || event.shiftKey || target?.closest('button, a, [role="button"]')) {
        return;
      }
      event.preventDefault();
      void finish();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cancel, finish, status]);

  return dictation;
}
